import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  ButtonBase,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useNavigate } from "react-router-dom";

import type { Person, Discipleship_Stage } from "api/Community/CommunityInterfaces";
import { peopleAPI } from "api/Community/PeopleAPI";
import { discipleshipStagesAPI } from "api/Community/DiscipleshipPathwayAPI";
import { RectangularTextButton } from "components/Base/Buttons";

export interface PersonDiscipleshipStageCardProps {
  person: Person;
  interactionToken?: string;
  includeLinkToDiscipleshipPathway?: boolean;
  stageDetailPath?: (stageId: number) => string;
  onPersonUpdated?: (updated: Person) => void;
  disabled?: boolean;
}

type SaveState = "idle" | "saving" | "success" | "error";
type AnyStage = any;

function extractStages(res: any): Discipleship_Stage[] {
  const raw = res?.data ?? res;
  const list =
    (Array.isArray(raw) && raw) ||
    (Array.isArray(raw?.results) && raw.results) ||
    (Array.isArray(raw?.items) && raw.items) ||
    (Array.isArray(raw?.data) && raw.data) ||
    [];

  return [...list]
    .filter(Boolean)
    .sort((a: AnyStage, b: AnyStage) => Number(a.id) - Number(b.id));
}

function stageLabel(stage: AnyStage | null | undefined): string | null {
  if (!stage) return null;

  const direct = stage?.functionality?.label;
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  const funcs = stage?.functionalities;
  if (Array.isArray(funcs) && funcs.length) {
    const labels = funcs
      .map((f: AnyStage) => (typeof f?.label === "string" ? f.label.trim() : ""))
      .filter(Boolean);
    if (labels.length) return labels.join(", ");
  }

  const chars = stage?.characteristics;
  if (Array.isArray(chars) && chars.length) {
    const labels = chars
      .map((c: AnyStage) =>
        typeof c?.label === "string"
          ? c.label.trim()
          : typeof c?.name === "string"
            ? c.name.trim()
            : ""
      )
      .filter(Boolean);
    if (labels.length) return labels.join(", ");
  }

  return null;
}

async function fetchStages(): Promise<any> {
  const api: any = discipleshipStagesAPI as any;

  if (typeof api.list === "function") return api.list();
  if (typeof api.getAll === "function") return api.getAll();
  if (typeof api.all === "function") return api.all();
  if (typeof api.fetch === "function") return api.fetch();
  if (typeof api.get === "function") return api.get();

  throw new Error("discipleshipStagesAPI has no list/getAll method.");
}


function StageButton({
  stage,
  disabled,
  onClick,
}: {
  stage: Discipleship_Stage;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <RectangularTextButton
      fullWidth
      colour="clear"
      buttonSize="small"
      disabled={disabled}
      onClick={onClick}
      textColor="inherit"
      borderColor="currentColor"
      sx={{
        height: "100%",
        minHeight: 64,
        px: { xs: 0.75, sm: 1.25 },
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Typography
        variant="button"
        align="center"
        sx={{
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: { xs: "0.62rem", sm: "0.72rem" },
          lineHeight: 1.15,
        }}
      >
        {stage.name}
      </Typography>
    </RectangularTextButton>
  );
}

export default function PersonDiscipleshipStageCard({
  person,
  interactionToken,
  includeLinkToDiscipleshipPathway = true,
  stageDetailPath = () => `/community/discipleship-pathway/`,
  onPersonUpdated,
  disabled = false,
}: PersonDiscipleshipStageCardProps) {
  const navigate = useNavigate();

  const currentStageId = person.discipleship_stage?.id ?? null;

  const [stages, setStages] = useState<Discipleship_Stage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedStageId, setSelectedStageId] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  useEffect(() => {
    setSelectedStageId(currentStageId ? String(currentStageId) : "");
  }, [currentStageId]);

  useEffect(() => {
    if (saveState === "success" || saveState === "error") {
      const t = window.setTimeout(() => setSaveState("idle"), 1200);
      return () => window.clearTimeout(t);
    }
  }, [saveState]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchStages();
        const list = extractStages(res);
        if (!cancelled) setStages(list);
      } catch (e: any) {
        if (!cancelled) {
          setStages([]);
          setError(e?.message || "Failed to load discipleship stages.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const { current, previous, next } = useMemo(() => {
    if (!currentStageId) return { current: null, previous: null, next: null };

    const idx = stages.findIndex((s) => Number(s.id) === Number(currentStageId));
    const fromList = idx >= 0 ? stages[idx] : null;

    const fallback = person.discipleship_stage?.id
      ? ({
          id: person.discipleship_stage.id,
          name: person.discipleship_stage.name,
          description: (person.discipleship_stage as any).description ?? "",
          functionality: (person.discipleship_stage as any).functionality,
          functionalities: (person.discipleship_stage as any).functionalities,
          characteristics: (person.discipleship_stage as any).characteristics,
        } as any as Discipleship_Stage)
      : null;

    const curr = fromList ?? fallback;

    return {
      current: curr,
      previous: idx > 0 ? stages[idx - 1] : null,
      next: idx >= 0 && idx < stages.length - 1 ? stages[idx + 1] : null,
    };
  }, [currentStageId, stages, person.discipleship_stage]);

  const previousLabel = useMemo(() => stageLabel(previous), [previous]);
  const nextLabel = useMemo(() => stageLabel(next), [next]);

  const updateStage = async (newStageId: number) => {
    const payload = { discipleship_stage_id: newStageId } as Partial<Person>;

    return interactionToken
      ? ((await peopleAPI.updateWithInteractionToken(
          person.id,
          payload,
          interactionToken
        )) as Person)
      : ((await peopleAPI.update(person.id, payload)) as Person);
  };

  const chooseStage = async (newStageId: number) => {
    if (disabled || isUpdating) return;

    try {
      setIsUpdating(true);
      setSaveState("saving");

      const updated = await updateStage(newStageId);

      setSaveState("success");
      onPersonUpdated?.(updated);
    } catch (e) {
      setSaveState("error");
      setSelectedStageId(currentStageId ? String(currentStageId) : "");
    } finally {
      setIsUpdating(false);
    }
  };

  if (!currentStageId) {
    return (
      <Box>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <FormControl
          fullWidth
          size="small"
          disabled={disabled || isUpdating || loading}
        >
          <InputLabel id="discipleship-stage-select-label">
            Select Discipleship Stage
          </InputLabel>
          <Select
            labelId="discipleship-stage-select-label"
            value={selectedStageId}
            label="Select Discipleship Stage"
            renderValue={(value) => {
              if (!value) {
                return loading ? "Loading stages..." : "Select Discipleship Stage";
              }

              return (
                stages.find((s) => String(s.id) === String(value))?.name ??
                "Select Discipleship Stage"
              );
            }}
            onChange={(e: SelectChangeEvent) => {
              const value = e.target.value;
              setSelectedStageId(value);

              const id = Number(value);
              if (!Number.isNaN(id) && id > 0) void chooseStage(id);
            }}
          >
            {loading ? (
              <MenuItem value="" disabled>
                Loading…
              </MenuItem>
            ) : stages.length === 0 ? (
              <MenuItem value="" disabled>
                No stages available
              </MenuItem>
            ) : (
              stages.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid2
        container
        spacing={{ xs: 0.75, sm: 1.25 }}
        alignItems="stretch"
        sx={{
          mt: 1,
          width: "100%",
        }}
      >
        {/* Previous stage */}
        <Grid2 size={{ xs: 3.5, sm: 3 }}>
          <Box
            sx={{
              height: "100%",
              minHeight: 64,
              display: "flex",
              alignItems: "stretch",
              opacity: previous?.id ? 0.75 : 0,
              transform: "scale(0.92)",
              transformOrigin: "right center",
              pointerEvents: previous?.id ? "auto" : "none",
              transition: (theme) =>
                theme.transitions.create(["transform", "opacity"], {
                  duration: theme.transitions.duration.short,
                }),
              "&:hover": previous?.id
                ? {
                    opacity: 1,
                    transform: "scale(0.96)",
                  }
                : undefined,
            }}
          >
            {previous?.id ? (
              <StageButton
                stage={previous}
                disabled={disabled || isUpdating}
                onClick={() => void chooseStage(previous.id)}
              />
            ) : null}
          </Box>
        </Grid2>

        {/* Current stage */}
        <Grid2 size={{ xs: 5, sm: 6 }}>
          <ButtonBase
            onClick={() =>
              current?.id &&
              includeLinkToDiscipleshipPathway &&
              navigate(stageDetailPath(current.id))
            }
            disabled={!current?.id}
            sx={{
              width: "100%",
              height: "100%",
              minHeight: 72,
              borderRadius: 2.5,
              border: 2,
              borderColor: "primary.main",
              bgcolor: "background.paper",
              px: { xs: 1, sm: 1.5 },
              py: 0,
              overflow: "hidden",
              boxShadow: 2,
              transition: (theme) =>
                theme.transitions.create(
                  ["transform", "box-shadow", "background-color"],
                  {
                    duration: theme.transitions.duration.short,
                  }
                ),
              "&:hover": {
                bgcolor: "action.hover",
                boxShadow: 4,
                transform: "translateY(-1px)",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 72,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                align="center"
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: {
                    xs: "1.1rem",
                    sm: "1.4rem",
                  },
                  lineHeight: 1.15,
                }}
              >
                {current?.name ?? "—"}
              </Typography>
            </Box>
          </ButtonBase>
        </Grid2>

        {/* Next stage */}
        <Grid2 size={{ xs: 3.5, sm: 3 }}>
          <Box
            sx={{
              height: "100%",
              minHeight: 64,
              display: "flex",
              alignItems: "stretch",
              opacity: next?.id ? 0.75 : 0,
              transform: "scale(0.92)",
              transformOrigin: "left center",
              pointerEvents: next?.id ? "auto" : "none",
              transition: (theme) =>
                theme.transitions.create(["transform", "opacity"], {
                  duration: theme.transitions.duration.short,
                }),
              "&:hover": next?.id
                ? {
                    opacity: 1,
                    transform: "scale(0.96)",
                  }
                : undefined,
            }}
          >
            {next?.id ? (
              <StageButton
                stage={next}
                disabled={disabled || isUpdating}
                onClick={() => void chooseStage(next.id)}
              />
            ) : null}
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
}